import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { useAssetModalState } from "../hooks/useAssetModalState";
import { useAssets } from "../hooks/useAssets";
import {
  useColorAssetForm,
  useColorPickerModalState,
  useColorPickerSave,
} from "../hooks/useColorPickerModal";
import { useImageUploadCommit } from "../hooks/useImageUploadCommit";
import { EMPTY_MESSAGE, TAB_LABEL } from "../lib/asset-modal-tabs";
import type { ParameterAssetSelection } from "../lib/parameter-asset";
import {
  selectionFromAsset,
  selectionFromColor,
  selectionFromUpload,
} from "../lib/parameter-asset";
import { galleryTreeQuery } from "../lib/queries";
import { AssetBrowserTab } from "./AssetBrowserTab";
import { AssetModalChrome, TitleWithSubject } from "./AssetModalChrome";
import { AssetPreviewLightbox } from "./AssetPreviewLightbox";
import { ColorAssetFormFields, ColorPickerFields } from "./ColorPickerFields";
import { ImageAssetUploadFields } from "./ImageAssetUploadFields";
import { SaveAsAssetCheckbox } from "./SaveAsAssetCheckbox";

type ParameterAssetTab = "assets" | "image" | "color";

const TABS = [
  { id: "assets" as ParameterAssetTab, label: TAB_LABEL.assets },
  { id: "image" as ParameterAssetTab, label: TAB_LABEL.image },
  { id: "color" as ParameterAssetTab, label: TAB_LABEL.color },
];

interface ParameterAssetModalProps {
  isOpen: boolean;
  /** Parameter the value is being chosen for, e.g. "frame color". */
  paramKey: string;
  /** Parameter group heading (e.g. "Upholstery") — shown for context. */
  groupName?: string;
  /** Seeds the color picker with the parameter's current value. */
  initialHex: string;
  /** Project of the refined image: a saved asset is attached to it, and the
   *  project's own assets are called out in the Assets tab. */
  projectId?: string | null;
  onSelect: (selection: ParameterAssetSelection) => void;
  onClose: () => void;
}

/** Primary-button state for the active tab. Each view finishes differently:
 *  the image tab uploads, the color tab may save an asset first, and the
 *  assets tab just needs a selection. */
function okButtonState(args: {
  tab: ParameterAssetTab;
  isUploading: boolean;
  hasFile: boolean;
  imageName: string;
  hasSelectedAsset: boolean;
  color: { okDisabled: boolean; okLabel: string };
}): { disabled: boolean; label: string } {
  if (args.tab === "image") {
    return {
      disabled:
        args.isUploading || !args.hasFile || args.imageName.trim().length === 0,
      label: args.isUploading ? "Adding…" : "Add",
    };
  }
  if (args.tab === "assets") {
    return { disabled: !args.hasSelectedAsset, label: "Ok" };
  }
  return { disabled: args.color.okDisabled, label: args.color.okLabel };
}

/** Resolve the folder name for the save-as-asset label. Project names only
 *  exist in the gallery tree (there is no single-project endpoint); while it
 *  loads the name is undefined and the label stays generic. */
function useFolderName(isOpen: boolean, projectId?: string | null) {
  const { orgId } = useAuth();
  const { data } = useQuery({
    ...galleryTreeQuery(orgId),
    enabled: isOpen && projectId != null,
  });
  if (!projectId) return null;
  return data?.projects?.find((p) => p.id === projectId)?.name ?? null;
}

/** Any asset can be applied to any parameter, so one modal serves them all:
 *  upload an image, pick a color, or reuse something already saved. */
export function ParameterAssetModal({
  isOpen,
  paramKey,
  groupName,
  initialHex,
  projectId,
  onSelect,
  onClose,
}: ParameterAssetModalProps) {
  const state = useAssetModalState<ParameterAssetTab>(isOpen, "assets");
  const folderName = useFolderName(isOpen, projectId);

  const isAssetsTab = state.activeTab === "assets";
  const isImageTab = state.activeTab === "image";
  const isColorTab = state.activeTab === "color";
  const assets = useAssets(isOpen && isAssetsTab);

  const pickerState = useColorPickerModalState({
    isOpen,
    initialHex,
    paramKey,
  });
  const colorForm = useColorAssetForm(isOpen);
  const colorSave = useColorPickerSave(
    {
      isOpen,
      isColorTab,
      groupName,
      onSave: (hex, asset) => onSelect(selectionFromColor(hex, asset)),
      onClose,
    },
    pickerState.selectedHex,
    colorForm,
  );

  const upload = useImageUploadCommit<ParameterAssetSelection>({
    isOpen,
    isImageTab,
    file: state.file,
    name: state.name,
    saveAsAsset: state.saveAsAsset,
    projectId,
    fromUpload: selectionFromUpload,
    fromAsset: selectionFromAsset,
    onCommit: (selection) => {
      onSelect(selection);
      onClose();
    },
  });

  if (!isOpen) return null;

  const selectedAsset = assets.assets.find(
    (a) => a.id === state.selectedAssetId,
  );
  const ok = okButtonState({
    tab: state.activeTab,
    isUploading: upload.isPending,
    hasFile: state.file !== null,
    imageName: state.name,
    hasSelectedAsset: selectedAsset !== undefined,
    color: colorSave,
  });

  const handleOk = () => {
    if (isImageTab) {
      upload.mutate();
      return;
    }
    if (isAssetsTab) {
      if (!selectedAsset) return;
      onSelect(selectionFromAsset(selectedAsset));
      onClose();
      return;
    }
    colorSave.handleOk();
  };

  const saveCheckbox = isImageTab ? (
    <SaveAsAssetCheckbox
      checked={state.saveAsAsset}
      onChange={state.setSaveAsAsset}
      label={folderName ? `Save image to ${folderName}` : "Save image as asset"}
    />
  ) : isColorTab ? (
    <SaveAsAssetCheckbox
      checked={colorForm.saveAsAsset}
      onChange={colorForm.setSaveAsAsset}
      label="Save color as asset"
    />
  ) : undefined;

  return (
    <>
      <AssetModalChrome
        ariaContext="add asset"
        title={
          <TitleWithSubject
            prefix="Add to"
            subject={pickerState.formattedParam}
            suffix="Parameter"
          />
        }
        subtitle={
          colorSave.formattedGroup ? (
            <>
              Select an asset for{" "}
              <strong className="font-semibold text-[var(--text-primary)]">
                {colorSave.formattedGroup}
              </strong>
              .
            </>
          ) : (
            "Select an asset for this parameter."
          )
        }
        tabs={TABS}
        activeTab={state.activeTab}
        onTabChange={state.setActiveTab}
        footerLeft={saveCheckbox}
        primary={{
          label: ok.label,
          disabled: ok.disabled,
          onClick: handleOk,
        }}
        onClose={onClose}
      >
        {isAssetsTab && (
          <AssetBrowserTab
            query={assets}
            projectId={projectId}
            enabled={isOpen && isAssetsTab}
            selectedAssetId={state.selectedAssetId}
            onSelect={(asset) => state.setSelectedAssetId(asset.id)}
            onPreview={state.setPreviewAsset}
            emptyMessage={EMPTY_MESSAGE.anyAsset}
          />
        )}

        {isImageTab && (
          <>
            <ImageAssetUploadFields
              file={state.file}
              previewUrl={state.previewUrl}
              onSelectFile={state.setFile}
              name={state.name}
              onNameChange={state.setName}
              onSubmit={() => {
                if (!ok.disabled) handleOk();
              }}
            />

            {upload.isError && (
              <p className="mt-4 text-sm text-red-400" role="alert">
                Couldn&apos;t use this image. Please try again.
              </p>
            )}
          </>
        )}

        {isColorTab && (
          <>
            <ColorPickerFields pickerState={pickerState} />
            {colorForm.saveAsAsset && (
              <ColorAssetFormFields assetForm={colorForm} />
            )}
            {colorSave.isError && (
              <p className="mt-4 text-sm text-red-400" role="alert">
                Couldn&apos;t save this color as an asset. Please try again.
              </p>
            )}
          </>
        )}
      </AssetModalChrome>

      <AssetPreviewLightbox
        asset={state.previewAsset}
        onClose={() => state.setPreviewAsset(null)}
        onUse={(asset) => {
          state.setSelectedAssetId(asset.id);
          state.setPreviewAsset(null);
        }}
      />
    </>
  );
}
